import { GenericTypeahead } from "../components/typeahead";
import { html } from "../utils/dom-utils";
import {
  AttachmentTypeOption,
  FileRowData,
  formatFileSize,
} from "./attachment-manager";
import { DropdownManager } from "./dropdown-manager";
import { ExistingFileInfo, FileInfo, FileWithSelection } from "./file-manager";

// Display layer
// Handle UI events of removing
// TODO: parse existing data?
export class AttachmentTableManager {
  private dropdownManager: DropdownManager;
  private typeaheadContainer: GenericTypeahead;
  private attachmentItems: HTMLElement;
  private fileCountSpan: HTMLElement;
  private totalSizeSpan: HTMLElement;
  private removeHandler: (btn: HTMLButtonElement) => void;
  private isHpdStaff: boolean;

  constructor(
    dropdownManager: DropdownManager,
    typeaheadContainer: GenericTypeahead,
    templateId: string,
    fileInputId: string,
    removeHandler: (btn: HTMLButtonElement) => void,
    isHpdStaff: boolean,
  ) {
    this.dropdownManager = dropdownManager;
    this.typeaheadContainer = typeaheadContainer;
    this.removeHandler = removeHandler;
    this.isHpdStaff = isHpdStaff;

    if (!document.getElementById(templateId)) {
      console.warn(
        `SyncTypeahead template '${templateId}' not found. Falling back to simple select.`,
      );
    }

    const attachmentList = document.getElementById(
      `${fileInputId}-attachment-list`,
    )!;
    attachmentList.style.display = "block";
    this.attachmentItems = attachmentList.querySelector(".attachment-items")!;
    this.fileCountSpan = attachmentList.querySelector(".file-count")!;
    this.totalSizeSpan = attachmentList.querySelector(".total-size")!;
  }

  public updateDisplay(info: FileInfo): void {
    this.updateFileCountAndSizeDisplay(info);
    this.rebuildFileTable(info);
    this.typeaheadContainer.init();
  }

  public updateAllDropdowns(fileInfo: FileInfo): void {
    // Update all existing attachment type dropdowns
    const attachmentTypeSelects = this.attachmentItems.querySelectorAll(
      ".attachment-type-cell",
    );

    attachmentTypeSelects.forEach((cell) => {
      const dropdown = cell.querySelector("select");
      const isNew = cell.getAttribute("data-file-index") != null;
      if (dropdown) {
        this.dropdownManager.updateDropdownOptions(
          dropdown as HTMLElement,
          !isNew,
        );
      }
    });
  }

  private updateFileCountAndSizeDisplay(info: FileInfo): void {
    this.fileCountSpan.textContent = `${info.fileCount} file${info.fileCount !== 1 ? "s" : ""} selected`;

    this.totalSizeSpan.textContent = formatFileSize(info.totalSize);
  }

  private rebuildFileTable(info: FileInfo): void {
    this.attachmentItems.innerHTML = "";
    this.addExistingFilesToTable(info.existingFiles);
    this.addNewFilesToTable(info.currentFiles);
  }

  private addExistingFilesToTable(existingFiles: ExistingFileInfo[]): void {
    existingFiles.forEach((existingFile) => {
      const fileRowData: FileRowData = { type: "existing", file: existingFile };
      // Get attachment type name from the dropdown manager's attachment types
      const attachmentTypeName = this.getAttachmentTypeName(
        existingFile.currentAttachmentTypeId,
      );
      const row = this.createFileRow(fileRowData, attachmentTypeName);
      this.attachmentItems.appendChild(row);
    });
  }

  private addNewFilesToTable(currentFiles: FileWithSelection[]): void {
    currentFiles.forEach((fileWithSelection, index) => {
      const fileRowData: FileRowData = {
        type: "new",
        file: fileWithSelection.file,
        index,
        currentAttachmentTypeId: fileWithSelection.currentAttachmentTypeId,
      };
      const attachmentTypeName = this.getAttachmentTypeName(
        fileWithSelection.currentAttachmentTypeId,
      );
      const row = this.createFileRow(fileRowData, attachmentTypeName);
      this.attachmentItems.appendChild(row);
    });
  }

  private createFileRow(
    fileRowData: FileRowData,
    attachmentTypeName?: string,
  ): HTMLElement {
    const row = this.createFileRowElement(fileRowData, attachmentTypeName);
    this.attachDropdownToRow(row, fileRowData);
    this.attachRemoveButtonHandler(row);
    return row;
  }

  private createFileRowElement(
    fileRow: FileRowData,
    attachmentTypeName?: string,
  ): HTMLElement {
    const isExisting = fileRow.type === "existing";
    const id = isExisting ? fileRow.file.id : null;
    const index = isExisting ? null : fileRow.index;
    const fileTypeDesc = getFileTypeDisplay(fileRow.file.name);

    const row = html`
      <tr class="dxbs-data-row" ${isExisting ? `data-attachment-id="${id}" data-file-size="${fileRow.file.size}"` : ""}>
        <td class="text-center">
          ${
            isExisting
              ? html`<button type="button" class="btn btn-sm btn-outline-danger attachment-remove-btn d-inline-block"
                    title="Remove file" data-existing-file-id="${id}"
                    >
              <i class="fas fa-times" aria-hidden="true"></i>
              Delete
            </button>
            `
              : html`<button type="button" class="btn btn-sm btn-outline-danger attachment-remove-btn d-inline-block"
                    title="Remove file" data-file-index="${index}"
                    >
              <i class="fas fa-times" aria-hidden="true"></i>
              Delete
            </button>
            `
          }
        </td>
        <td class="text-left">
          <div class="attachment-name fw-medium">
            ${
              isExisting
                ? this.shouldShowDownloadLink(attachmentTypeName)
                  ? html`<a
                    href="/api/download/${id}/view/${fileRow.file.name}"
                    target="_blank">
                    ${fileRow.file.name}
                  </a>`
                  : html`<span class="text-muted" title="Download restricted - HPD staff only">${fileRow.file.name}</span>`
                : html`<a class="new-file-download-link" target="_blank" href="javascript:void()">${fileRow.file.name}</a>`
            }
          </div>
          <small class="attachment-size text-muted">${formatFileSize(fileRow.file.size)}</small>
        </td>
        <td class="text-left">${fileTypeDesc}</td>
        <td class="text-left attachment-type-cell" ${!index ? `data-file-id="${id}"` : `data-file-index="${index}"`}>
          ${isExisting ? html`<input type="hidden" name="ExistingFileIds" value="${id}" />` : ""}
          <!-- Dropdown will be inserted here -->
        </td>
      </tr>
    `;

    if (row instanceof Array) throw Error("Should only get one element");
    if (!isExisting) {
      Array.from(row.getElementsByClassName("new-file-download-link")).forEach(
        (link) => {
          link.addEventListener("click", () => {
            const url = URL.createObjectURL(fileRow.file);
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          });
        },
      );
    }
    return row;
  }

  private attachDropdownToRow(row: HTMLElement, fileInfo: FileRowData): void {
    const isExisting = fileInfo.type === "existing";
    const attachmentTypeCell = row.querySelector(
      ".attachment-type-cell",
    ) as HTMLElement;
    const dropdownId = isExisting
      ? `existing-attachment-type-${fileInfo.file.id}`
      : `attachment-type-${fileInfo.index}`;

    const currentAttachmentTypeId = isExisting
      ? fileInfo.file.currentAttachmentTypeId
      : fileInfo.currentAttachmentTypeId;

    const dropdown = this.dropdownManager.createDropdown(
      dropdownId,
      isExisting ? null : fileInfo.index,
      isExisting ? fileInfo.file.id : undefined,
      currentAttachmentTypeId,
    );

    attachmentTypeCell.appendChild(dropdown);

    const hiddenInput = dropdown.querySelector(
      'input[type="hidden"]',
    ) as HTMLInputElement;
    if (hiddenInput) {
      this.typeaheadContainer.resetInput(hiddenInput.id);
    }
  }

  private attachRemoveButtonHandler(row: HTMLElement): void {
    const removeBtn = row.querySelector(
      ".attachment-remove-btn",
    ) as HTMLButtonElement;

    removeBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      this.removeHandler(removeBtn);
    });
  }

  private shouldShowDownloadLink(attachmentTypeName?: string): boolean {
    // If user is HPD staff, always show download link
    if (this.isHpdStaff) {
      return true;
    }

    // If no attachment type name provided, show download link by default
    if (!attachmentTypeName) {
      return true;
    }

    // If attachment type is restricted and user is not HPD staff, hide download link
    return !isHpdRestrictedAttachmentType(attachmentTypeName);
  }

  public getAttachmentTypeName(attachmentTypeId?: string): string | undefined {
    if (!attachmentTypeId) {
      return undefined;
    }

    // Get attachment types from the dropdown manager
    return this.dropdownManager.getAttachmentTypeName(
      parseInt(attachmentTypeId),
    );
  }
}

// Static lookup map for file type display
// Based on Lookup.FileType
const FILE_TYPE_MAP: { [key: string]: string } = {
  pdf: "PDF document",
  doc: "Word document",
  docx: "Word document",
  xls: "Excel Document",
  xlsx: "Excel spreadsheet",
  txt: "Text document",
  text: "Text document",
  jpg: "JPG image",
  jpeg: "JPG image",
  png: "PNG image",
  jfif: "JPG image",
  pjpeg: "JPG image",
  pjp: "JPG image",
};

function getFileTypeDisplay(fileName: string): string {
  const extension = fileName.includes(".")
    ? fileName.split(".")?.pop()?.toLowerCase()
    : undefined;
  return extension ? FILE_TYPE_MAP[extension] : "Unknown file type";
}

// Attachment types that require HPD staff access
export const HPD_RESTRICTED_ATTACHMENT_TYPES = [
  "SHPO Directory Application",
  "Part 1 Application",
  "Part 1 Amendment Application",
  "Part 2 Application",
  "Individual Account Application",
  "Transcript(s)/Diploma",
];

export function isHpdRestrictedAttachmentType(
  attachmentTypeName: string,
): boolean {
  return HPD_RESTRICTED_ATTACHMENT_TYPES.includes(attachmentTypeName);
}

const DEFAULT_MAX_FILE_COUNT = 10;
const DEFAULT_MAX_FILE_SIZE_BYTES = 10485760; // 10MB
const DEFAULT_FILE_EXTENSIONS = "";

export interface AttachmentInputInfo {
  maxFileCount: number;
  maxFileSizeBytes: number;
  acceptedTypes: string[];
  isRequired: boolean;
  attachmentTypes: AttachmentTypeOption[];
  projectProgramAreaId: number;
  existingFiles: ExistingFileInfo[];
  isHpdStaff: boolean;
}

export function parseAttachmentInput(fileInput: HTMLInputElement) {
  const maxFileCount = parseInt(
    fileInput.getAttribute("data-max-file-count") ||
      DEFAULT_MAX_FILE_COUNT.toString(),
  );
  const maxFileSizeBytes = parseInt(
    fileInput.getAttribute("data-max-file-size") ||
      DEFAULT_MAX_FILE_SIZE_BYTES.toString(),
  );
  const acceptedTypes = (
    fileInput.getAttribute("data-accepted-types") || DEFAULT_FILE_EXTENSIONS
  ).split(",");
  const isRequired = fileInput.getAttribute("data-required") === "true";

  const attachmentTypes = parseAttachmentTypes(fileInput);
  const projectProgramAreaId = parseProgramAreaId(fileInput);
  const existingFiles = parseExistingFiles(fileInput.id);
  const isHpdStaff = fileInput.getAttribute("data-is-hpd-staff") === "true";

  return {
    maxFileCount,
    maxFileSizeBytes,
    acceptedTypes,
    isRequired,
    attachmentTypes,
    projectProgramAreaId,
    existingFiles,
    isHpdStaff,
  };
}

function parseAttachmentTypes(input: HTMLInputElement): AttachmentTypeOption[] {
  const attachmentTypesData = input.getAttribute("data-attachment-types");
  if (attachmentTypesData) {
    try {
      return JSON.parse(attachmentTypesData);
    } catch (e) {
      console.error("Failed to parse attachment types:", e);
    }
  }
  return [];
}

function parseProgramAreaId(input: HTMLInputElement): number | null {
  const projectProgramAreaIdData = input.getAttribute(
    "data-project-program-area-id",
  );
  if (projectProgramAreaIdData) {
    return parseInt(projectProgramAreaIdData, 10) || null;
  }
  return null;
}

function parseExistingFiles(fileInputId: string): ExistingFileInfo[] {
  const attachmentList = document.getElementById(
    `${fileInputId}-attachment-list`,
  )!;
  const attachmentItems = attachmentList.querySelector(".attachment-items")!;
  // Read existing file rows from the DOM (server-rendered)
  const existingRows = Array.from(
    attachmentItems.querySelectorAll("tr.dxbs-data-row"),
  ) as HTMLTableRowElement[];

  return existingRows.map((row) => {
    const nameElement = row.querySelector(".attachment-name") as HTMLDivElement;
    // Handle both 2-column and 3-column layouts defensively
    // const fileTypeCell = row.cells.length >= 3 ? row.cells[2] : row.cells[1];
    const idAttribute = row.getAttribute("data-attachment-id") || "";
    const fileSizeAttribute = row.getAttribute("data-file-size") || "0";

    // Extract current attachment type from the select element
    const attachmentTypeSelect = row.querySelector(
      ".attachment-type-select",
    ) as HTMLSelectElement;
    const currentAttachmentTypeId = attachmentTypeSelect?.value || undefined;

    return {
      id: idAttribute,
      name: nameElement?.textContent?.trim() || "",
      size: parseInt(fileSizeAttribute, 10),
      currentAttachmentTypeId,
    };
  });
}
